import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
  Subscription,
  Subject,
  merge,
  interval,
  startWith,
  switchMap,
  catchError,
  EMPTY,
} from 'rxjs';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { EmptyResultComponent } from '../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { StatusService } from '../services/status.service';
import {
  AggregatedStatusResponse,
  OverallHealthStatus,
  ServiceHealthStatus,
  StatusServiceRow,
} from '../models/status.model';

const REFRESH_MS = 30_000;

@Component({
  selector: 'app-admin-status',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatChipsModule,
    TitleBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
  ],
  templateUrl: './admin-status.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-status.component.scss'],
})
export class AdminStatusComponent implements OnInit, OnDestroy {
  loading = true;
  refreshing = false;
  errorMessage = '';
  overall: OverallHealthStatus | null = null;
  checkedAt: string | null = null;
  cached = false;
  services: StatusServiceRow[] = [];

  get refreshIcon(): string {
    return this.refreshing || this.loading ? 'pi pi-refresh pi-spin' : 'pi pi-refresh';
  }

  readonly shimmerColumns: ShimmerColumn[] = [
    { label: 'Service', width: 'lg' },
    { label: 'Status', width: 'sm' },
    { label: 'Latency', width: 'sm' },
    { label: 'Detail', width: 'md' },
  ];

  private readonly refresh$ = new Subject<void>();
  private statusSub?: Subscription;

  constructor(private readonly statusService: StatusService) {}

  ngOnInit(): void {
    // Single stream so poll and manual refresh cannot race; switchMap drops in-flight work.
    this.statusSub = merge(interval(REFRESH_MS).pipe(startWith(0)), this.refresh$)
      .pipe(
        switchMap(() => {
          if (!this.loading) {
            this.refreshing = true;
          }
          return this.statusService.getAggregatedStatus().pipe(
            catchError(err => {
              this.applyRefreshError(err);
              return EMPTY;
            })
          );
        })
      )
      .subscribe(payload => this.applyPayload(payload));
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe();
    this.refresh$.complete();
  }

  refresh(): void {
    this.refresh$.next();
  }

  overallLabel(status: OverallHealthStatus | null): string {
    if (status === 'ok') return 'All systems operational';
    if (status === 'degraded') return 'Degraded — some non-critical services are down';
    if (status === 'down') return 'Outage — one or more critical services are down';
    return 'Status any';
  }

  overallSeverity(status: OverallHealthStatus | null): 'success' | 'warn' | 'danger' | 'secondary' {
    if (status === 'ok') return 'success';
    if (status === 'degraded') return 'warn';
    if (status === 'down') return 'danger';
    return 'secondary';
  }

  serviceSeverity(status: ServiceHealthStatus): 'success' | 'danger' {
    return status === 'ok' ? 'success' : 'danger';
  }

  serviceLabel(status: ServiceHealthStatus): string {
    return status === 'ok' ? 'OK' : 'Down';
  }

  private applyPayload(payload: AggregatedStatusResponse): void {
    this.loading = false;
    this.refreshing = false;
    this.errorMessage = '';
    this.overall = payload.overall;
    this.checkedAt = payload.checkedAt;
    this.cached = !!payload.cached;
    this.services = payload.services ?? [];
  }

  private applyRefreshError(err: any): void {
    this.loading = false;
    this.refreshing = false;
    const anyErr = err as { error?: { error?: string }; message?: string };
    this.errorMessage = anyErr?.error?.error || anyErr?.message || 'Unable to load service status';
  }
}
